import { styled } from 'solid-styled-components';
import { COLORS, SPACING, TYPOGRAPHY, ANIMATION } from '../../../constants/design-tokens';

export const FieldRoot = styled('div')`
  min-height: 100vh;
  background: ${COLORS.background.primary};
  color: ${COLORS.text.primary};
  font-family: ${TYPOGRAPHY.fontFamily.mono.join(', ')};
`;

export const StatusBar = styled('header')`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING[4]};
  padding: ${SPACING[3]} ${SPACING[5]};
  background: ${COLORS.background.dark};
  border-bottom: 1px solid ${COLORS.phosphor.greenMute};
  font-size: ${TYPOGRAPHY.fontSize.sm};
  letter-spacing: 0.04em;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: ${SPACING[3]} ${SPACING[4]};
  }
`;

export const StatusBrand = styled('span')`
  font-family: ${TYPOGRAPHY.fontFamily.display.join(', ')};
  color: ${COLORS.phosphor.green};
  text-shadow: 0 0 12px ${COLORS.phosphor.bloom};
`;

export const StatusNav = styled('nav')`
  display: flex;
  gap: ${SPACING[4]};
  flex-wrap: wrap;

  a {
    color: ${COLORS.phosphor.greenDim};
    text-decoration: none;
    text-transform: lowercase;
  }

  a:hover,
  a:focus-visible {
    color: ${COLORS.phosphor.green};
  }
`;

export const FieldMain = styled('main')`
  max-width: 960px;
  margin: 0 auto;
  padding: ${SPACING[10]} ${SPACING[5]} ${SPACING[16]};

  @media (max-width: 768px) {
    padding: ${SPACING[8]} ${SPACING[4]} ${SPACING[12]};
  }
`;

export const Section = styled('section')`
  margin-bottom: ${SPACING[16]};
  scroll-margin-top: 4.5rem;
`;

export const SectionLabel = styled('p')`
  margin: 0 0 ${SPACING[4]};
  color: ${COLORS.phosphor.greenMute};
  font-size: ${TYPOGRAPHY.fontSize.xs};
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

export const DisplayName = styled('h1')`
  margin: 0 0 ${SPACING[3]};
  font-family: ${TYPOGRAPHY.fontFamily.display.join(', ')};
  font-size: clamp(2rem, 6vw, 3.25rem);
  font-weight: ${TYPOGRAPHY.fontWeight.normal};
  color: ${COLORS.phosphor.green};
  text-shadow: 0 0 18px ${COLORS.phosphor.bloom};
  line-height: ${TYPOGRAPHY.lineHeight.tight};
`;

export const PromptLockup = styled('p')`
  margin: 0 0 ${SPACING[6]};
  color: ${COLORS.phosphor.greenDim};
  font-size: ${TYPOGRAPHY.fontSize.sm};
`;

export const Greeting = styled('p')`
  margin: 0 0 ${SPACING[8]};
  max-width: 42rem;
  font-size: ${TYPOGRAPHY.fontSize.base};
  line-height: ${TYPOGRAPHY.lineHeight.relaxed};
  color: ${COLORS.text.primary};
`;

export const SkillBands = styled('div')`
  display: grid;
  gap: ${SPACING[5]};
`;

export const SkillRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING[2]};
  align-items: baseline;
`;

export const SkillKey = styled('span')`
  font-size: ${TYPOGRAPHY.fontSize.xs};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(props) =>
    props.band === 'amber' ? COLORS.phosphor.amberDim : COLORS.phosphor.greenMute};
  margin-right: ${SPACING[2]};
`;

export const SkillLink = styled('a')`
  text-decoration: none;
  font-size: ${TYPOGRAPHY.fontSize.sm};
  color: ${(props) => (props.band === 'amber' ? COLORS.phosphor.amber : COLORS.phosphor.green)};
  border: 1px solid
    ${(props) => (props.band === 'amber' ? COLORS.phosphor.amberMute : COLORS.phosphor.greenMute)};
  padding: 0.15rem 0.45rem;

  &:hover {
    background: ${(props) =>
      props.band === 'amber' ? 'rgba(232, 195, 106, 0.12)' : 'rgba(198, 232, 154, 0.12)'};
  }
`;

export const SkillChip = styled('span')`
  font-size: ${TYPOGRAPHY.fontSize.sm};
  color: ${(props) => (props.band === 'amber' ? COLORS.phosphor.amber : COLORS.phosphor.green)};
  border: 1px solid
    ${(props) => (props.band === 'amber' ? COLORS.phosphor.amberMute : COLORS.phosphor.greenMute)};
  padding: 0.15rem 0.45rem;
`;

export const BandGrid = styled('div')`
  display: grid;
  gap: ${SPACING[8]};
  align-items: start;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: ${SPACING[6]};
  }
`;

export const BandColumn = styled('div')`
  border: 1px solid
    ${(props) => (props.band === 'amber' ? COLORS.phosphor.amberMute : COLORS.phosphor.greenMute)};
  padding: ${SPACING[4]};
  background: ${COLORS.background.secondary};
`;

export const BandTitle = styled('h2')`
  margin: 0 0 ${SPACING[4]};
  font-size: ${TYPOGRAPHY.fontSize.sm};
  font-weight: ${TYPOGRAPHY.fontWeight.medium};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${(props) => (props.band === 'amber' ? COLORS.phosphor.amber : COLORS.phosphor.green)};
`;

export const ProjectRow = styled('article')`
  border-top: 1px solid
    ${(props) =>
      props.band === 'amber' ? 'rgba(232, 195, 106, 0.18)' : 'rgba(198, 232, 154, 0.18)'};
  padding: ${SPACING[3]} 0;
`;

export const ProjectToggle = styled('button')`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: ${SPACING[3]};
  background: transparent;
  border: 0;
  padding: ${SPACING[2]} 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover .project-name {
    text-decoration: underline;
  }
`;

export const ProjectName = styled('span')`
  color: ${(props) => (props.band === 'amber' ? COLORS.phosphor.amber : COLORS.phosphor.green)};
`;

export const ProjectMeta = styled('span')`
  color: ${COLORS.text.tertiary};
  font-size: ${TYPOGRAPHY.fontSize.xs};
  white-space: nowrap;
`;

export const ProjectBody = styled('div')`
  padding: 0 0 ${SPACING[3]};
  color: ${COLORS.text.secondary};
  font-size: ${TYPOGRAPHY.fontSize.sm};
  line-height: ${TYPOGRAPHY.lineHeight.relaxed};
`;

export const Bezel = styled('div')`
  margin: ${SPACING[3]} 0;
  border: 2px solid
    ${(props) => (props.band === 'amber' ? COLORS.phosphor.amberMute : COLORS.phosphor.greenMute)};
  background: ${COLORS.background.dark};
  padding: ${SPACING[2]};
`;

export const BezelScreen = styled('div')`
  overflow: hidden;
  background: #050705;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  video {
    display: block;
    width: 100%;
    max-height: 280px;
    object-fit: contain;
    filter: ${(props) => (props.live ? 'none' : 'grayscale(0.55) saturate(0.5)')};
    transition: filter ${ANIMATION.duration.slow} ${ANIMATION.easing.easeOut};
  }

  @media (prefers-reduced-motion: reduce) {
    img,
    video {
      filter: none;
    }
  }
`;

export const ProjectLinks = styled('div')`
  display: flex;
  gap: ${SPACING[4]};
  margin-top: ${SPACING[3]};

  a {
    color: ${(props) => (props.band === 'amber' ? COLORS.phosphor.amber : COLORS.phosphor.green)};
    text-decoration: none;
    font-size: ${TYPOGRAPHY.fontSize.sm};
  }

  a:hover {
    text-decoration: underline;
  }
`;

export const WorkHead = styled('div')`
  border: 1px solid ${COLORS.phosphor.greenMute};
  padding: ${SPACING[4]};
  background: ${COLORS.background.secondary};
`;

export const WorkTitle = styled('h2')`
  margin: 0 0 ${SPACING[2]};
  font-size: ${TYPOGRAPHY.fontSize.lg};
  font-weight: ${TYPOGRAPHY.fontWeight.medium};
  color: ${COLORS.phosphor.green};
`;

export const WorkNodeHeading = styled('h3')`
  margin: 0 0 ${SPACING[2]};
  font-size: ${TYPOGRAPHY.fontSize.base};
  font-weight: ${TYPOGRAPHY.fontWeight.medium};
  color: ${COLORS.phosphor.green};
`;

export const WorkStamp = styled('p')`
  margin: 0 0 ${SPACING[3]};
  color: ${COLORS.phosphor.greenMute};
  font-size: ${TYPOGRAPHY.fontSize.xs};
  letter-spacing: 0.08em;
`;

export const WorkLinks = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING[3]};
  margin: 0 0 ${SPACING[3]};

  a {
    color: ${COLORS.phosphor.greenDim};
    text-decoration: none;
    font-size: ${TYPOGRAPHY.fontSize.sm};
  }

  a:hover,
  a:focus-visible {
    color: ${COLORS.phosphor.green};
    text-decoration: underline;
  }
`;

export const WorkList = styled('ul')`
  margin: 0;
  padding-left: ${SPACING[5]};
  color: ${COLORS.text.secondary};
  font-size: ${TYPOGRAPHY.fontSize.sm};
  line-height: ${TYPOGRAPHY.lineHeight.relaxed};
`;

export const TreeToggle = styled('button')`
  margin-top: ${SPACING[4]};
  background: transparent;
  border: 1px solid ${COLORS.phosphor.greenMute};
  color: ${COLORS.phosphor.green};
  font: inherit;
  font-size: ${TYPOGRAPHY.fontSize.sm};
  padding: ${SPACING[2]} ${SPACING[3]};
  cursor: pointer;

  &:hover {
    background: rgba(198, 232, 154, 0.1);
  }
`;

export const Tree = styled('ol')`
  list-style: none;
  margin: ${SPACING[6]} 0 0;
  padding: 0;

  &[data-collapsed='true'] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }
`;

export const TrunkNode = styled('li')`
  position: relative;
  padding-left: ${SPACING[6]};
  margin-bottom: ${SPACING[6]};
  border-left: 1px solid ${COLORS.phosphor.greenMute};

  &::before {
    content: '';
    position: absolute;
    left: -4px;
    top: 0.35rem;
    width: 7px;
    height: 7px;
    background: ${COLORS.phosphor.green};
    box-shadow: 0 0 8px ${COLORS.phosphor.bloom};
  }
`;

export const SideNodes = styled('ul')`
  list-style: none;
  margin: ${SPACING[3]} 0 0;
  padding: 0;
`;

export const SideNode = styled('li')`
  position: relative;
  margin: ${SPACING[3]} 0 0 ${SPACING[4]};
  padding-left: ${SPACING[4]};
  border-left: 1px dashed ${COLORS.phosphor.amberMute};
  color: ${COLORS.text.secondary};
  font-size: ${TYPOGRAPHY.fontSize.sm};

  &::before {
    content: '';
    position: absolute;
    left: -3px;
    top: 0.4rem;
    width: 5px;
    height: 5px;
    background: ${COLORS.phosphor.amber};
  }
`;

export const SideTitle = styled('strong')`
  display: block;
  color: ${COLORS.phosphor.amber};
  font-weight: ${TYPOGRAPHY.fontWeight.medium};
  margin-bottom: ${SPACING[1]};
`;

export const ContactRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING[3]};
`;

export const ContactAction = styled('button')`
  background: transparent;
  border: 1px solid ${COLORS.phosphor.green};
  color: ${COLORS.phosphor.green};
  font: inherit;
  padding: ${SPACING[2]} ${SPACING[4]};
  cursor: pointer;

  &:hover {
    background: rgba(198, 232, 154, 0.12);
  }
`;

export const ContactLink = styled('a')`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${COLORS.phosphor.greenMute};
  color: ${COLORS.phosphor.greenDim};
  text-decoration: none;
  padding: ${SPACING[2]} ${SPACING[4]};

  &:hover {
    color: ${COLORS.phosphor.green};
    border-color: ${COLORS.phosphor.green};
  }
`;

export const FieldFoot = styled('p')`
  margin: ${SPACING[16]} 0 0;
  color: ${COLORS.phosphor.greenMute};
  font-size: ${TYPOGRAPHY.fontSize.xs};
  letter-spacing: 0.12em;
`;
